import { useMutation, useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";
import { toast } from "react-toastify";

import { HTTPError, patch, post } from "../helpers/HTTP";

import type { AgencyPlan, Promotion } from "../types";

async function createSubscriptionRequest(
  count: number,
  paymentSourceId: string,
  planPricingId: string,
): Promise<AgencyPlan> {
  return (
    await post(`subscriptions`, {
      seat_count: count,
      payment_source_id: paymentSourceId,
      item_price_id: planPricingId,
    })
  ).promotion;
}
async function updateSubscriptionRequest(
  count: number,
  paymentSourceId: string,
  planPricingId: string,
): Promise<AgencyPlan> {
  return (
    await patch(`subscriptions`, {
      seat_count: count,
      payment_source_id: paymentSourceId,
      item_price_id: planPricingId,
    })
  ).promotion;
}

export default function useCustomerSubscriptionMutations() {
  const queryClient = useQueryClient();

  const { mutateAsync: createSubscription, isPending: isCreatingSubscription } =
    useMutation({
      mutationFn: ({
        count,
        paymentSourceId,
        planPricingId,
      }: {
        count: number;
        paymentSourceId: string;
        planPricingId: string;
      }) => createSubscriptionRequest(count, paymentSourceId, planPricingId),
      onSuccess: (promotion) => {
        queryClient.setQueryData<Promotion[]>(
          ["promotions"],
          (prevPromotions) => {
            if (prevPromotions) {
              return [...prevPromotions, promotion];
            } else {
              return [promotion];
            }
          },
        );
      },
      onError: (error) =>
        toast.error(
          error instanceof HTTPError && error.serverMessage
            ? error.serverMessage
            : "Failed to create subscription",
        ),
    });

  const { mutateAsync: updateSubscription, isPending: isUpdatingSubscription } =
    useMutation({
      mutationFn: ({
        count,
        paymentSourceId,
        planPricingId,
      }: {
        count: number;
        paymentSourceId: string;
        planPricingId: string;
      }) => updateSubscriptionRequest(count, paymentSourceId, planPricingId),
      onSuccess: (updatedPlan) => {
        queryClient.setQueryData<Promotion[]>(
          ["promotions"],
          (prevPromotions) =>
            produce(prevPromotions, (promotionsDraft) => {
              for (let i = 0; i < promotionsDraft.length; i += 1) {
                const promotion = promotionsDraft[i];
                if (
                  promotion.type === "agency" &&
                  promotion.subscription.id === updatedPlan.subscription.id
                ) {
                  promotionsDraft[i] = updatedPlan;
                  return promotionsDraft;
                }
              }
            }),
        );
        queryClient.invalidateQueries({
          queryKey: ["estimateRenewal"],
        });
      },
      onError: (error) =>
        toast.error(
          error instanceof HTTPError && error.serverMessage
            ? error.serverMessage
            : "Failed to update subscription",
        ),
    });

  return {
    createSubscription,
    isCreatingSubscription,
    updateSubscription,
    isUpdatingSubscription,
  };
}
